import type { Audience, UserRef } from './auth.types';

// ─── Content status ────────────────────────────────────────────────────────────

export type ContentStatus = 'not_started' | 'in_progress' | 'completed' | 'published';

export const CONTENT_STATUS_LABELS: Record<ContentStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed:   'Completed',
  published:   'Published',
};

/** Tailwind colour classes for each status. */
export const CONTENT_STATUS_COLORS: Record<ContentStatus, string> = {
  not_started: 'text-slate-500 bg-slate-100',
  in_progress: 'text-amber-700 bg-amber-100',
  completed:   'text-emerald-700 bg-emerald-100',
  published:   'text-indigo-700 bg-indigo-100',
};

export const CONTENT_STATUS_DOT: Record<ContentStatus, string> = {
  not_started: 'bg-slate-400',
  in_progress: 'bg-amber-400',
  completed:   'bg-emerald-400',
  published:   'bg-indigo-400',
};

// ─── Daily content record ──────────────────────────────────────────────────────

/**
 * A single daily content record as stored in Firestore.
 * Path: pregnancyContent/{audience}/days/{dayNumber padded to 3 digits}
 */
export interface DailyContent {
  // Identity
  audience:    Audience;
  dayNumber:   number;
  weekNumber:  number;
  monthNumber: number;

  // Textual content — English
  title:          string | null;
  description:    string | null;
  meditation:     string | null;
  prayer:         string | null;
  bibleVerse:     string | null;
  bibleReference: string | null;

  // Textual content — Malayalam (മലയാളം)
  titleMl:          string | null;
  descriptionMl:    string | null;
  meditationMl:     string | null;
  prayerMl:         string | null;
  bibleVerseMl:     string | null;
  bibleReferenceMl: string | null;

  // Media
  youtubeUrl:       string | null;
  audioUrl:         string | null;
  audioStoragePath: string | null;
  imageUrl:         string | null;
  imageStoragePath: string | null;

  // Status
  status: ContentStatus;

  // Audit metadata
  createdBy:  UserRef | null;
  createdAt:  Date | null;
  updatedBy:  UserRef | null;
  updatedAt:  Date | null;
}

/**
 * The writable fields of a daily content record (excludes computed identity fields).
 * Used when saving partial content.
 */
export type DailyContentInput = Partial<
  Pick<
    DailyContent,
    | 'title'
    | 'description'
    | 'meditation'
    | 'prayer'
    | 'bibleVerse'
    | 'bibleReference'
    | 'titleMl'
    | 'descriptionMl'
    | 'meditationMl'
    | 'prayerMl'
    | 'bibleVerseMl'
    | 'bibleReferenceMl'
    | 'youtubeUrl'
    | 'audioUrl'
    | 'audioStoragePath'
    | 'imageUrl'
    | 'imageStoragePath'
    | 'status'
  >
>;

/** All content field keys, used for copy-content field selection. */
export const CONTENT_FIELD_KEYS = [
  'title',
  'description',
  'youtubeUrl',
  'audioUrl',
  'meditation',
  'prayer',
  'bibleVerse',
  'bibleReference',
  'titleMl',
  'descriptionMl',
  'meditationMl',
  'prayerMl',
  'bibleVerseMl',
  'bibleReferenceMl',
] as const;

export type ContentFieldKey = typeof CONTENT_FIELD_KEYS[number];

export const CONTENT_FIELD_LABELS: Record<ContentFieldKey, string> = {
  title:            'Title (English)',
  description:      'Description (English)',
  youtubeUrl:       'YouTube Video',
  audioUrl:         'Audio File',
  meditation:       'Meditation (English)',
  prayer:           'Prayer (English)',
  bibleVerse:       'Bible Verse (English)',
  bibleReference:   'Bible Reference (English)',
  titleMl:          'Title (Malayalam)',
  descriptionMl:    'Description (Malayalam)',
  meditationMl:     'Meditation (Malayalam)',
  prayerMl:         'Prayer (Malayalam)',
  bibleVerseMl:     'Bible Verse (Malayalam)',
  bibleReferenceMl: 'Bible Reference (Malayalam)',
};

// ─── Content history ───────────────────────────────────────────────────────────

export interface ContentHistoryEntry {
  id:        string;
  changedBy: UserRef;
  changedAt: Date;
  action:    'created' | 'updated' | 'status_changed' | 'copied';
  changedFields:   string[];
  previousValues:  Partial<DailyContent>;
  newValues:       Partial<DailyContent>;
  snapshot:        Partial<DailyContent>;
}

// ─── Progress / completion ─────────────────────────────────────────────────────

export interface DayProgress {
  dayNumber: number;
  status:    ContentStatus;
}

export interface WeekProgress {
  weekNumber: number;
  days:       DayProgress[];
  completed:  number;
  total:      number;
  percentage: number;
}

export interface MonthProgress {
  monthNumber: number;
  weeks:       WeekProgress[];
  completed:   number;
  total:       number;
  percentage:  number;
}

export interface AudienceProgress {
  audience:   Audience;
  months:     MonthProgress[];
  completed:  number;
  total:      number;
  percentage: number;
}

export interface OverallProgress {
  mothers:     AudienceProgress;
  fathers:     AudienceProgress;
  grandparents:AudienceProgress;
  totalCompleted: number;
  grandTotal:     number;
  percentage:     number;
}

// ─── Content copy ──────────────────────────────────────────────────────────────

export type CopyConflictStrategy = 'cancel' | 'empty_only' | 'replace';

export interface CopyContentOptions {
  sourceAudience: Audience;
  targetAudience: Audience;
  dayNumber:      number;
  fields:         ContentFieldKey[];
  conflictStrategy: CopyConflictStrategy;
}
